// Orb data: the week's planned things (METIS agenda rows + KYRS scheduled tasks),
// the user's protocols, METIS miss reasons and CLIO entry type. One loader per
// orb mount; every write in actions.ts ends with reload().
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useDataVersion } from '../../live/screenContext';
import { parseETCToMinutes, parseScheduled } from '../../lib/autoSchedule';
import { readNav, readNavSync, writeNav } from '../../lib/navPersist';
import { SECTORS, type Sector } from '../../domain/olmps';
import { useCompassConfig } from '../../olmps/sectorConfig';
import { weekStartOf } from '../../olmps/gameSettings';
import type { UseTodayData } from '../../olmps/useTodayData';
import { addDaysKey, dateKeyOf, keyToDate, parseAgendaTime } from './time';
import type { Notes, ObjNote, OrbData, OrbItem, ProtocolLite } from './types';

interface RawAgenda {
  id: string;
  item_id: string;
  date: string;
  text: string | null;
  time: string | null;
  duration: string | null;
  completed: boolean | null;
  missed_reason: string | null;
  sort_order: number | null;
  sector: string | null;
  task_id: string | null;
  design_id: string | null;
}
interface RawTask {
  id: string;
  title: string | null;
  scheduled: string | null;
  done: boolean | null;
  unlisted: boolean | null;
  sector: string | null;
  design_id: string | null;
  list_id: string | null;
  etc: string | null;
}

const asSector = (v: string | null | undefined): Sector | null => (v && (SECTORS as string[]).includes(v) ? (v as Sector) : null);
const FULL_SCHED = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/;

export function useOrbData(userId: string | undefined, _today: UseTodayData, now: Date): OrbData {
  const { config } = useCompassConfig(userId);
  const game = config.game;
  const ledger = config.ledger;
  const todayKey = dateKeyOf(now);
  const weekStart = useMemo(() => weekStartOf(keyToDate(todayKey), game), [todayKey, game]);
  const weekStartKey = dateKeyOf(weekStart);
  const weekEndKey = addDaysKey(weekStartKey, 6);
  const afterKey = addDaysKey(weekStartKey, 7);

  const [agenda, setAgenda] = useState<RawAgenda[]>([]);
  const [tasks, setTasks] = useState<RawTask[]>([]);
  const [listSector, setListSector] = useState<Record<string, Sector>>({});
  const [protocols, setProtocols] = useState<ProtocolLite[]>([]);
  const [missReasons, setMissReasons] = useState<string[]>([]);
  const [clio, setClio] = useState<OrbData['clio']>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((n) => n + 1), []);
  // An agent wrote records: reload the orb's rows too.
  const dataVersion = useDataVersion();

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }
    let live = true;
    (async () => {
      try {
        const [ag, tk, ls, pr, st] = await Promise.all([
          supabase
            .from('agenda_items')
            .select('id,item_id,date,text,time,duration,completed,missed_reason,sort_order,sector,task_id,design_id')
            .eq('user_id', userId)
            .gte('date', weekStartKey)
            .lte('date', weekEndKey),
          supabase
            .from('tasks')
            .select('id,title,scheduled,done,unlisted,sector,design_id,list_id,etc')
            .eq('user_id', userId)
            .not('scheduled', 'is', null)
            .gte('scheduled', weekStartKey)
            .lt('scheduled', afterKey),
          supabase.from('task_lists').select('id,olmps_sector').eq('user_id', userId),
          supabase.from('protocols').select('id,title,notes').eq('user_id', userId),
          supabase.from('user_settings').select('metis_miss_reasons,custom_journal_entry_types').eq('user_id', userId).maybeSingle(),
        ]);
        if (!live) return;
        setAgenda((ag.data ?? []) as RawAgenda[]);
        setTasks((tk.data ?? []) as RawTask[]);
        const map: Record<string, Sector> = {};
        for (const l of (ls.data ?? []) as { id: string; olmps_sector: string | null }[]) {
          const s = asSector(l.olmps_sector);
          if (s) map[l.id] = s;
        }
        setListSector(map);
        setProtocols((pr.data ?? []) as ProtocolLite[]);
        const row = st.data as { metis_miss_reasons?: unknown; custom_journal_entry_types?: unknown } | null;
        setMissReasons(Array.isArray(row?.metis_miss_reasons) ? (row!.metis_miss_reasons as unknown[]).filter((r): r is string => typeof r === 'string') : []);
        const types = Array.isArray(row?.custom_journal_entry_types) ? (row!.custom_journal_entry_types as any[]) : [];
        let found: OrbData['clio'] = null;
        for (const ty of types) {
          const p = Array.isArray(ty?.prompts) ? ty.prompts.find((x: any) => x && x.type !== 'dropdown' && typeof x.id === 'string') : null;
          if (ty?.id && p) {
            found = { typeId: String(ty.id), promptId: String(p.id) };
            break;
          }
        }
        setClio(found);
      } catch (e) {
        console.warn('[orb] load failed', e);
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => {
      live = false;
    };
  }, [userId, weekStartKey, weekEndKey, afterKey, tick, dataVersion]);

  const items = useMemo<OrbItem[]>(() => {
    const out: OrbItem[] = [];
    const taskById = new Map(tasks.map((t) => [t.id, t]));
    const sectorOfTask = (t: RawTask | undefined) => (t ? asSector(t.sector) ?? (t.list_id ? listSector[t.list_id] ?? null : null) : null);
    const seenTask = new Set<string>();
    for (const a of agenda) {
      if (!a.text) continue;
      const base = keyToDate(a.date);
      const start = parseAgendaTime(a.time, base);
      const mins = Number.parseInt(a.duration ?? '', 10);
      const t = a.task_id ? taskById.get(a.task_id) : undefined;
      if (a.task_id) seenTask.add(`${a.task_id}|${a.date}`);
      out.push({
        key: `agenda:${a.id}`,
        source: 'agenda',
        rowId: a.id,
        itemId: a.item_id,
        taskId: a.task_id ?? null,
        title: a.text,
        date: a.date,
        hasTime: !!start,
        start,
        minutes: Number.isFinite(mins) && mins > 0 ? mins : 30,
        completed: !!a.completed,
        missed: a.missed_reason ?? '',
        sector: asSector(a.sector) ?? sectorOfTask(t),
        designId: a.design_id ?? null,
        sort: a.sort_order ?? 0,
        scheduledRaw: t?.scheduled ?? null,
      });
    }
    for (const t of tasks) {
      if (t.unlisted || !t.title) continue;
      const at = parseScheduled(t.scheduled);
      if (!at) continue;
      const date = dateKeyOf(at);
      if (seenTask.has(`${t.id}|${date}`)) continue;
      const hasTime = !!t.scheduled && FULL_SCHED.test(t.scheduled);
      out.push({
        key: `task:${t.id}`,
        source: 'task',
        rowId: null,
        itemId: null,
        taskId: t.id,
        title: t.title,
        date,
        hasTime,
        start: hasTime ? at : null,
        minutes: parseETCToMinutes(t.etc) ?? 30,
        completed: !!t.done,
        missed: '',
        sector: sectorOfTask(t),
        designId: t.design_id ?? null,
        sort: 1000,
        scheduledRaw: t.scheduled,
      });
    }
    out.sort((a, b) => (a.date === b.date ? (a.start?.getTime() ?? Infinity) - (b.start?.getTime() ?? Infinity) || a.sort - b.sort : a.date < b.date ? -1 : 1));
    return out;
  }, [agenda, tasks, listSector]);

  const weekDays = useMemo(() => {
    const out: { key: string; date: Date }[] = [];
    for (let i = 0; i < 7; i++) {
      const key = addDaysKey(weekStartKey, i);
      const date = keyToDate(key);
      if (game.countWeekends || game.weekDays.includes(date.getDay() as 0)) out.push({ key, date });
    }
    return out;
  }, [weekStartKey, game]);

  return { loading, game, ledger, items, weekDays, weekStartKey, weekEndKey, protocols, missReasons, clio, tick, reload };
}

/** Items belonging to a sector's orb. Untagged items show only when nothing is tagged for the sector. */
export function itemsForSector(items: OrbItem[], sector: Sector | null): OrbItem[] {
  if (!sector) return items;
  const tagged = items.filter((i) => i.sector === sector);
  return tagged.length ? tagged : items.filter((i) => !i.sector);
}

/** Objective corrections + lessons the user typed on the orb (device-local; the DB has no field for them). */
export function useOrbNotes(): Notes {
  const [map, setMap] = useState<Record<string, ObjNote>>(() => readNavSync<Record<string, ObjNote>>('orbnotes') ?? {});
  const ref = useRef(map);
  ref.current = map;
  useEffect(() => {
    void readNav<Record<string, ObjNote>>('orbnotes').then((v) => {
      if (v) setMap((cur) => ({ ...v, ...cur }));
    });
  }, []);
  return useMemo<Notes>(
    () => ({
      get: (key) => map[key],
      set: (key, v) => {
        const next = { ...ref.current, [key]: v };
        ref.current = next;
        setMap(next);
        writeNav('orbnotes', next);
      },
    }),
    [map],
  );
}
